type BrandInkPreloaderProps = {
  /** Visible caption under the stroke (also exposed to assistive tech when announce is true) */
  label?: string
  className?: string
  /** When false, stroke is decorative; parent should expose loading (e.g. section aria-live). */
  announce?: boolean
  size?: "default" | "compact"
}

const INK_VIEWBOX = "0 0 80 40"

const INK_MAIN_PATH =
  "M 6 28 C 12 14 18 32 26 22 C 34 12 40 28 48 20 C 54 14 58 24 64 18"
const INK_ACCENT_PATH = "M 62 20 Q 68 28 74 22"
const INK_GUIDE_PATH = "M 4 32 Q 28 30 52 32 T 76 30"

const InkStrokeSvg = ({ compact }: { compact: boolean }) => {
  const box = compact ? "h-14 w-[4.375rem]" : "h-16 w-20"
  const mainStroke = compact ? 2.25 : 2.75
  const accentStroke = compact ? 1.75 : 2.25
  const guideStroke = compact ? 1 : 1.25

  return (
    <svg viewBox={INK_VIEWBOX} className={`${box} shrink-0 overflow-visible`} aria-hidden fill="none">
      <path
        d={INK_GUIDE_PATH}
        pathLength={100}
        stroke="currentColor"
        strokeWidth={guideStroke}
        strokeLinecap="round"
        className="text-neutral-200 motion-reduce:text-neutral-300"
        strokeDasharray="100"
        strokeDashoffset={0}
      />
      <path
        d={INK_MAIN_PATH}
        pathLength={100}
        stroke="currentColor"
        strokeWidth={mainStroke}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-neutral-900 motion-safe:animate-brand-ink-draw motion-reduce:[stroke-dashoffset:0] motion-reduce:opacity-90"
        strokeDasharray="100"
      />
      <path
        d={INK_ACCENT_PATH}
        pathLength={100}
        stroke="currentColor"
        strokeWidth={accentStroke}
        strokeLinecap="round"
        className="text-brand-500 motion-safe:animate-brand-ink-accent motion-reduce:[stroke-dashoffset:0] motion-reduce:opacity-90"
        strokeDasharray="100"
      />
      <circle
        cx={6}
        cy={28}
        r={compact ? 1.75 : 2.25}
        className="fill-neutral-900 motion-safe:animate-brand-ink-dot motion-reduce:opacity-80"
      />
    </svg>
  )
}

/**
 * Handwritten ink stroke: SVG paths draw via stroke-dashoffset — no spinning circles.
 */
export const BrandInkPreloader = ({
  label,
  className = "",
  announce = true,
  size = "default",
}: BrandInkPreloaderProps) => {
  const isCompact = size === "compact"
  const stackGap = isCompact ? "gap-2" : "gap-4"
  const labelClass = isCompact ? "text-xs font-medium" : "text-sm font-medium"

  const body = (
    <>
      <InkStrokeSvg compact={isCompact} />
      {label ? <span className={labelClass}>{label}</span> : null}
    </>
  )

  if (!announce) {
    return (
      <div
        className={`flex flex-col items-center justify-center ${stackGap} text-gray-600 ${className}`}
        aria-hidden
      >
        {body}
      </div>
    )
  }

  return (
    <div
      className={`flex flex-col items-center justify-center ${stackGap} text-gray-600 ${className}`}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      {body}
    </div>
  )
}
